import './senior-statistics-dashboard.scss';
import {Alert, Box, Button, Card, Divider, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {MutableRefObject, useCallback, useEffect, useRef, useState} from 'react';
import {PageHeader} from '../page-header/page-header';
import {translations} from '../../i18n/translations';
import {TextChart} from '../charts/text-chart/text-chart';
import {TableChart, TableChartRows} from '../charts/table-chart/table-chart';
import {AreaChart, AreaChartData} from '../charts/area-chart/area-chart';
import {PieChart, PieChartData} from '../charts/pie-chart/pie-chart';
import {StackChart, StackChartData} from '../charts/stack-chart/stack-chart';
import {LineChart, LineChartData} from '../charts/line-chart/line-chart';
import {StatisticsRepository} from '../../repositories/statistics';
import {CallDirection} from '../../backend/enums/call-direction';
import {TransmissionDirection} from '../../backend/enums/transmission-direction';
import {MediaType} from '../../backend/enums/media-type';
import {chartColors} from '../../utils/constants/colors';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {CategorizedApplication} from '../../backend/responses/categorized-application';
import {ApplicationsRepository} from '../../repositories/applications';
import {appIdentifications} from '../../utils/constants/identifications';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {UsersRepository} from '../../repositories/users';
import {
  FlatSearchSelectInput,
  SearchSelectOption,
} from '../flat-search-select-input/flat-search-select-input';
import {getStatisticsBinningByDates} from '../../utils/functions/get-statistics-binning';
import {getXLabelStatisticsDashboardChart} from '../../utils/functions/get-x-label-statistics-dashboard-chart';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment, {Moment} from 'moment';

enum TopPreferencesColumnIdentifier {
  Activity = 'activity',
  Total = 'total',
}

type SeniorStatisticsDashboardProps = {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  seniorId: string;
  startDate: Moment;
  endDate: Moment;
  showTitle?: boolean;
  entityId?: string;
  notShowUpdateButton?: boolean;
};

/**
 * Senior statistics dashboard.
 */
export const SeniorStatisticsDashboard = (props: SeniorStatisticsDashboardProps) => {
  // Hold state
  const [numberInformalCaregivers, setNumberInformalCaregivers] = useState<number | undefined>();
  const [loadingNumberInformalCaregivers, setLoadingNumberInformalCaregivers] =
    useState<boolean>(true);
  const [errorNumberInformalCaregivers, setErrorNumberInformalCaregivers] = useState<
    string | undefined
  >();
  const [numberPendingInvitations, setNumberPendingInvitations] = useState<number | undefined>();
  const [loadingNumberPendingInvitations, setLoadingNumberPendingInvitations] =
    useState<boolean>(true);
  const [errorNumberPendingInvitations, setErrorNumberPendingInvitations] = useState<
    string | undefined
  >();
  const [averageDailyUsage, setAverageDailyUsage] = useState<number | undefined>();
  const [loadingAverageDailyUsage, setLoadingAverageDailyUsage] = useState<boolean>(true);
  const [errorAverageDailyUsage, setErrorAverageDailyUsage] = useState<string | undefined>();
  const [lastAccess, setLastAccess] = useState<string | undefined>();
  const [loadingLastAccess, setLoadingLastAccess] = useState<boolean>(true);
  const [errorLastAccess, setErrorLastAccess] = useState<string | undefined>();
  const [numberCalls, setNumberCalls] = useState<number | undefined>();
  const [loadingNumberCalls, setLoadingNumberCalls] = useState<boolean>(true);
  const [errorNumberCallsLast, setErrorNumberCallsLast] = useState<string | undefined>();
  const [numberIncomingCallsLast, setNumberIncomingCallsLast] = useState<number | undefined>();
  const [loadingNumberIncomingCallsLast, setLoadingNumberIncomingCallsLast] =
    useState<boolean>(true);
  const [errorNumberIncomingCallsLast, setErrorNumberIncomingCallsLast] = useState<
    string | undefined
  >();
  const [numberOutgoingCallsLast, setNumberOutgoingCallsLast] = useState<number | undefined>();
  const [loadingNumberOutgoingCallsLast, setLoadingNumberOutgoingCallsLast] =
    useState<boolean>(true);
  const [errorNumberOutgoingCallsLast, setErrorNumberOutgoingCallsLast] = useState<
    string | undefined
  >();
  const [numberVideoMessagesSent, setNumberVideoMessagesSent] = useState<number | undefined>();
  const [loadingNumberVideoMessagesSent, setLoadingNumberVideoMessagesSent] =
    useState<boolean>(true);
  const [errorNumberVideoMessagesSent, setErrorNumberVideoMessagesSent] = useState<
    string | undefined
  >();
  const [numberVideoMessagesReceived, setNumberVideoMessagesReceived] = useState<
    number | undefined
  >();
  const [loadingNumberVideoMessagesReceived, setLoadingNumberVideoMessagesReceived] =
    useState<boolean>(true);
  const [errorNumberVideoMessagesReceived, setErrorNumberVideoMessagesReceived] = useState<
    string | undefined
  >();
  const [numberPhotosSent, setNumberPhotosSent] = useState<number | undefined>();
  const [loadingNumberPhotosSent, setLoadingNumberPhotosSent] = useState<boolean>(true);
  const [errorNumberPhotosSent, setErrorNumberPhotosSent] = useState<string | undefined>();
  const [numberDrawingsSent, setNumberDrawingsSent] = useState<number | undefined>();
  const [loadingNumberDrawingsSent, setLoadingNumberDrawingsSent] = useState<boolean>(true);
  const [errorNumberDrawingsSent, setErrorNumberDrawingsSent] = useState<string | undefined>();
  const [numberPaintingsSent, setNumberPaintingsSent] = useState<number | undefined>();
  const [loadingNumberPaintingsSent, setLoadingNumberPaintingsSent] = useState<boolean>(true);
  const [errorNumberPaintingsSent, setErrorNumberPaintingsSent] = useState<string | undefined>();
  const [numberMediaReceived, setNumberMediaReceived] = useState<number | undefined>();
  const [loadingNumberMediaReceived, setLoadingNumberMediaReceived] = useState<boolean>(true);
  const [errorNumberMediaReceived, setErrorNumberMediaReceived] = useState<string | undefined>();
  const [topPreferences, setTopPreferences] = useState<TableChartRows>([]);
  const [loadingTopPreferences, setLoadingTopPreferences] = useState<boolean>(true);
  const [errorTopPreferences, setErrorTopPreferences] = useState<string | undefined>();
  const [generalSystemUsage, setGeneralSystemUsage] = useState<AreaChartData>([]);
  const [loadingGeneralSystemUsage, setLoadingGeneralSystemUsage] = useState<boolean>(true);
  const [errorGeneralSystemUsage, setErrorGeneralSystemUsage] = useState<string | undefined>();
  const [mostUsedApplications, setMostUsedApplicationsLast] = useState<PieChartData>([]);
  const [loadingMostUsedApplications, setLoadingMostUsedApplications] = useState<boolean>(true);
  const [errorMostUsedApplications, setErrorMostUsedApplications] = useState<string | undefined>();
  const [applicationsUsage, setApplicationsUsage] = useState<StackChartData>({
    data: [],
    coordinatesData: [],
  });
  const [loadingApplicationsUsage, setLoadingApplicationsUsage] = useState<boolean>(true);
  const [errorApplicationsUsage, setErrorApplicationsUsage] = useState<string | undefined>();
  // ----- Per application charts -----
  const [selectedUserAffiliation, setSelectedUserAffiliation] = useState<
    AffiliationInfo | undefined
  >();
  const [selectedUserApplication, setSelectedUserApplication] = useState<
    CategorizedApplication | undefined
  >();
  const [applicationUsage, setApplicationUsage] = useState<
    | {
        applicationId: string;
        applicationName: string;
        data: AreaChartData;
      }
    | undefined
  >();
  const [loadingApplicationUsage, setLoadingApplicationUsage] = useState<boolean>(false);
  const [errorApplicationUsage, setErrorApplicationUsage] = useState<string | undefined>();
  const [applicationEvolution, setApplicationEvolution] = useState<
    | {
        applicationId: string;
        applicationName: string;
        data: LineChartData;
      }
    | undefined
  >();
  const [loadingApplicationEvolution, setLoadingApplicationEvolution] = useState<boolean>(false);
  const [errorApplicationEvolution, setErrorApplicationEvolution] = useState<string | undefined>();

  // Entity id
  const entityIdRef: MutableRefObject<string | undefined> = useRef();

  // Translations
  const {t} = useTranslation();

  // Get the number of informal caregivers
  const getNumberInformalCaregivers = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberInformalCaregivers(true);

      // Clear error
      setErrorNumberInformalCaregivers(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getInformalCaregiversCountForUser(
        props.seniorId
      );

      // Set value
      setNumberInformalCaregivers(result.informalCaregiversCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of informal caregivers for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setNumberInformalCaregivers(undefined);

      // Set error
      setErrorNumberInformalCaregivers(error.message);
    } finally {
      // Stop loading
      setLoadingNumberInformalCaregivers(false);
    }
  }, [props.seniorId]);

  // Get the number of pending invitations
  const getNumberPendingInvitations = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberPendingInvitations(true);

      // Clear error
      setErrorNumberPendingInvitations(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getInProgressInvitesCountForUser(
        props.seniorId
      );

      // Set value
      setNumberPendingInvitations(result.inProgressInvitesCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of pending invitations for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setNumberPendingInvitations(undefined);

      // Set error
      setErrorNumberPendingInvitations(error.message);
    } finally {
      // Stop loading
      setLoadingNumberPendingInvitations(false);
    }
  }, [props.seniorId]);

  // Get the average daily usage: last X days
  const getAverageDailyUsage = useCallback(async () => {
    try {
      // Start loading
      setLoadingAverageDailyUsage(true);

      // Clear error
      setErrorAverageDailyUsage(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getSystemAverageUsageForUser(
        props.seniorId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString())
      );

      // Set value
      setAverageDailyUsage(result.averageTimeInHours);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the average daily usage for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setAverageDailyUsage(undefined);

      // Set error
      setErrorAverageDailyUsage(error.message);
    } finally {
      // Stop loading
      setLoadingAverageDailyUsage(false);
    }
  }, [props.seniorId, props.endDate, props.startDate]);

  // Get the last access
  const getLastAccess = useCallback(async () => {
    try {
      // Start loading
      setLoadingLastAccess(true);

      // Clear error
      setErrorLastAccess(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getLatestAccessForUser(props.seniorId);

      // Set value
      setLastAccess(result.accessDate ? moment(result.accessDate).format('L LT') : undefined);
    } catch (error: any) {
      // Log
      console.debug(`🔴 Could not get the last access for senior ${props.seniorId}.`, error);

      // Set value
      setLastAccess(undefined);

      // Set error
      setErrorLastAccess(error.message);
    } finally {
      // Stop loading
      setLoadingLastAccess(false);
    }
  }, [props.seniorId]);

  // Get the number of calls: last X days
  const getNumberCalls = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberCalls(true);

      // Clear error
      setErrorNumberCallsLast(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getCallsCountForUser(
        props.seniorId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString())
      );

      // Set value
      setNumberCalls(result.callsCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of calls for senior ${props.seniorId} in the last 30 days.`,
        error
      );

      // Set value
      setNumberCalls(undefined);

      // Set error
      setErrorNumberCallsLast(error.message);
    } finally {
      // Stop loading
      setLoadingNumberCalls(false);
    }
  }, [props.endDate, props.seniorId, props.startDate]);

  // Get the number of incoming calls: last X days
  const getNumberIncomingCalls = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberIncomingCallsLast(true);

      // Clear error
      setErrorNumberIncomingCallsLast(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getCallsCountForUser(
        props.seniorId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        CallDirection.CaregiverToSenior
      );

      // Set value
      setNumberIncomingCallsLast(result.callsCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of incoming calls for senior ${props.seniorId} in the last 30 days.`,
        error
      );

      // Set value
      setNumberIncomingCallsLast(undefined);

      // Set error
      setErrorNumberIncomingCallsLast(error.message);
    } finally {
      // Stop loading
      setLoadingNumberIncomingCallsLast(false);
    }
  }, [props.endDate, props.seniorId, props.startDate]);

  // Get the number of outgoing calls: last X days
  const getNumberOutgoingCalls = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberOutgoingCallsLast(true);

      // Clear error
      setErrorNumberOutgoingCallsLast(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getCallsCountForUser(
        props.seniorId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        CallDirection.SeniorToCaregiver
      );

      // Set value
      setNumberOutgoingCallsLast(result.callsCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of outgoing calls for senior ${props.seniorId} in the last 30 days.`,
        error
      );

      // Set value
      setNumberOutgoingCallsLast(undefined);

      // Set error
      setErrorNumberOutgoingCallsLast(error.message);
    } finally {
      // Stop loading
      setLoadingNumberOutgoingCallsLast(false);
    }
  }, [props.endDate, props.seniorId, props.startDate]);

  // Get the number of video messages sent
  const getNumberVideoMessagesSent = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberVideoMessagesSent(true);

      // Clear error
      setErrorNumberVideoMessagesSent(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getVideoMessagesCountForUser(
        props.seniorId,
        TransmissionDirection.Sent
      );

      // Set value
      setNumberVideoMessagesSent(result.videoMessagesCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of video messages sent for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setNumberVideoMessagesSent(undefined);

      // Set error
      setErrorNumberVideoMessagesSent(error.message);
    } finally {
      // Stop loading
      setLoadingNumberVideoMessagesSent(false);
    }
  }, [props.seniorId]);

  // Get the number of messages video received
  const getNumberVideoMessagesReceived = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberVideoMessagesReceived(true);

      // Clear error
      setErrorNumberVideoMessagesReceived(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getVideoMessagesCountForUser(
        props.seniorId,
        TransmissionDirection.Received
      );

      // Set value
      setNumberVideoMessagesReceived(result.videoMessagesCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of video messages received for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setNumberVideoMessagesReceived(undefined);

      // Set error
      setErrorNumberVideoMessagesReceived(error.message);
    } finally {
      // Stop loading
      setLoadingNumberVideoMessagesReceived(false);
    }
  }, [props.seniorId]);

  // Get the number of photos sent
  const getNumberPhotosSent = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberPhotosSent(true);

      // Clear error
      setErrorNumberPhotosSent(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getMediaCountForUser(
        props.seniorId,
        TransmissionDirection.Sent,
        [MediaType.Photo]
      );

      // Set value
      setNumberPhotosSent(result.mediaCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of photos sent for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setNumberPhotosSent(undefined);

      // Set error
      setErrorNumberPhotosSent(error.message);
    } finally {
      // Stop loading
      setLoadingNumberPhotosSent(false);
    }
  }, [props.seniorId]);

  // Get the number of drawings sent
  const getNumberDrawingsSent = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberDrawingsSent(true);

      // Clear error
      setErrorNumberDrawingsSent(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getMediaCountForUser(
        props.seniorId,
        TransmissionDirection.Sent,
        [MediaType.Drawing]
      );

      // Set value
      setNumberDrawingsSent(result.mediaCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of drawings sent for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setNumberDrawingsSent(undefined);

      // Set error
      setErrorNumberDrawingsSent(error.message);
    } finally {
      // Stop loading
      setLoadingNumberDrawingsSent(false);
    }
  }, [props.seniorId]);

  // Get the number of paintings sent
  const getNumberPaintingsSent = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberPaintingsSent(true);

      // Clear error
      setErrorNumberPaintingsSent(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getMediaCountForUser(
        props.seniorId,
        TransmissionDirection.Sent,
        [MediaType.Painting]
      );

      // Set value
      setNumberPaintingsSent(result.mediaCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of paintings sent for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setNumberPaintingsSent(undefined);

      // Set error
      setErrorNumberPaintingsSent(error.message);
    } finally {
      // Stop loading
      setLoadingNumberPaintingsSent(false);
    }
  }, [props.seniorId]);

  // Get the number of media received
  const getNumberMediaReceived = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberMediaReceived(true);

      // Clear error
      setErrorNumberMediaReceived(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getMediaCountForUser(
        props.seniorId,
        TransmissionDirection.Received,
        [MediaType.Photo, MediaType.Video]
      );

      // Set value
      setNumberMediaReceived(result.mediaCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of media received for senior ${props.seniorId}.`,
        error
      );

      // Set value
      setNumberMediaReceived(undefined);

      // Set error
      setErrorNumberMediaReceived(error.message);
    } finally {
      // Stop loading
      setLoadingNumberMediaReceived(false);
    }
  }, [props.seniorId]);

  // Get the top preferences: last X days
  const getTopPreferences = useCallback(async () => {
    try {
      // Start loading
      setLoadingTopPreferences(true);

      // Clear error
      setErrorTopPreferences(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getTopPreferencesForUser(
        props.seniorId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        true
      );

      // Process
      const processedData: TableChartRows = result.data.map((obj) => {
        return {
          id: obj.activityType.id || '',
          columns: [
            {
              columnId: TopPreferencesColumnIdentifier.Activity,
              value: obj.activityType.name,
            },
            {columnId: TopPreferencesColumnIdentifier.Total, value: obj.durationInHours},
          ],
        };
      });

      // Set value
      setTopPreferences(processedData);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the top preferences for the senior ${props.seniorId} in the last 30 days.`,
        error
      );

      // Set value
      setTopPreferences([]);

      // Set error
      setErrorTopPreferences(error.message);
    } finally {
      // Stop loading
      setLoadingTopPreferences(false);
    }
  }, [props.endDate, props.seniorId, props.startDate]);

  // Get the general system usage: last X days
  const getGeneralSystemUsage = useCallback(async () => {
    try {
      // Start loading
      setLoadingGeneralSystemUsage(true);

      // Clear error
      setErrorGeneralSystemUsage(undefined);

      // Update time lapse
      setGeneralSystemUsage([]);

      // Get the binning
      const binning = getStatisticsBinningByDates(props.startDate, props.endDate);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getSystemUsageForUser(
        props.seniorId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        binning
      );

      // Process
      const processedResult: AreaChartData = result.data.map((obj) => {
        return {
          y: obj.durationInHours,
          x: getXLabelStatisticsDashboardChart(obj.month - 1, obj.year, result.binning, obj.date),
        };
      });

      // Set value
      setGeneralSystemUsage(processedResult);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the general system usage for senior ${props.seniorId}.`,
        error
      );

      // Set error
      setErrorGeneralSystemUsage(error.message);
    } finally {
      // Stop loading
      setLoadingGeneralSystemUsage(false);
    }
  }, [props.endDate, props.seniorId, props.startDate]);

  // Get the most used applications: last X days
  const getMostUsedApplications = useCallback(async () => {
    try {
      // Start loading
      setLoadingMostUsedApplications(true);

      // Clear error
      setErrorMostUsedApplications(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getMostUsedApplicationsForUser(
        props.seniorId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        true
      );

      // Process
      let processedResult: PieChartData = [];
      let colorIndex: number = 0;
      for (let i = 0; i < result.data.length; i++) {
        // Get element
        const element = result.data[i];

        // Color to used
        let chartColor: string = '';
        if (colorIndex < chartColors.length) {
          // Set color
          chartColor = chartColors[colorIndex];

          // Increment
          colorIndex += 1;
        } else {
          // Set color
          chartColor = chartColors[0];

          // Reset
          colorIndex = 1;
        }

        // Add to processed result
        processedResult.push({
          id: element.application.id || '',
          y: element.usageTimeInHours,
          x: element.application.name,
          color: chartColor,
        });
      }

      // Set value
      setMostUsedApplicationsLast(processedResult);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the most used applications for senior ${props.seniorId} in the last 30 days.`,
        error
      );

      // Set value
      setMostUsedApplicationsLast([]);

      // Set error
      setErrorMostUsedApplications(error.message);
    } finally {
      // Stop loading
      setLoadingMostUsedApplications(false);
    }
  }, [props.endDate, props.seniorId, props.startDate]);

  // Get the applications usage: last X days
  const getApplicationsUsage = useCallback(async () => {
    try {
      // Start loading
      setLoadingApplicationsUsage(true);

      // Clear error
      setErrorApplicationsUsage(undefined);

      // Update time lapse
      setApplicationsUsage({
        data: [],
        coordinatesData: [],
      });

      // Get the binning
      const binning = getStatisticsBinningByDates(props.startDate, props.endDate);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getSystemUsagePerApplicationForUser(
        props.seniorId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        undefined,
        true,
        binning
      );

      // Get the x values
      let xValues: Array<string> = [];
      for (let i = 0; i < result.data.length; i++) {
        // Value x
        const xValue: string = getXLabelStatisticsDashboardChart(
          result.data[i].month - 1,
          result.data[i].year,
          result.binning,
          result.data[i].date
        );

        // Add x value
        if (!xValues.find((label) => label === xValue)) {
          xValues.push(xValue);
        }
      }

      // Process
      let processedLabels: StackChartData['coordinatesData'] = [];
      let processedData: StackChartData['data'] = [];
      let colorIndex: number = 0;
      for (let i = 0; i < result.data.length; i++) {
        // Get element
        const element = result.data[i];

        // Add to processed result
        if (
          element.application.id &&
          !processedData.find((obj) => obj.id === element.application.id)
        ) {
          // Color to used
          let chartColor: string = '';
          if (colorIndex < chartColors.length) {
            // Set color
            chartColor = chartColors[colorIndex];

            // Increment
            colorIndex += 1;
          } else {
            // Set color
            chartColor = chartColors[0];

            // Reset
            colorIndex = 1;
          }

          // Data
          processedData.push({
            id: element.application.id,
            label: element.application.name,
            color: chartColor,
            values: xValues.map((label) => {
              // Get the y value
              const yValue: number =
                result.data.find(
                  (obj) =>
                    obj.application.id === element.application.id &&
                    getXLabelStatisticsDashboardChart(
                      obj.month - 1,
                      obj.year,
                      result.binning,
                      obj.date
                    ) === label
                )?.usageTimeInHours || 0;

              // Process labels
              let coordinateIndex = processedLabels.findIndex(
                (obj) => obj.x === label && obj.y === yValue
              );
              if (coordinateIndex !== -1) {
                processedLabels[coordinateIndex] = {
                  ...processedLabels[coordinateIndex],
                  stacks: [
                    ...processedLabels[coordinateIndex].stacks,
                    {
                      stackId: element.application.id as string,
                      stackLabel: element.application.name,
                    },
                  ],
                };
              } else {
                processedLabels.push({
                  x: label,
                  y: yValue,
                  stacks: [
                    {
                      stackId: element.application.id as string,
                      stackLabel: element.application.name,
                    },
                  ],
                });
              }

              return {
                x: label,
                y: yValue,
              };
            }),
          });
        }
      }

      // Set value
      setApplicationsUsage({
        data: processedData,
        coordinatesData: processedLabels,
      });
    } catch (error: any) {
      // Log
      console.debug(`🔴 Could not get the applications usage for senior ${props.seniorId}.`, error);

      // Set error
      setErrorApplicationsUsage(error.message);
    } finally {
      // Stop loading
      setLoadingApplicationsUsage(false);
    }
  }, [props.endDate, props.seniorId, props.startDate]);

  // Get the application usage: last X days
  const getApplicationUsage = useCallback(
    async (applicationId: string, applicationName: string) => {
      try {
        // Start loading
        setLoadingApplicationUsage(true);

        // Clear error
        setErrorApplicationUsage(undefined);

        // Update the application
        setApplicationUsage({
          applicationId: applicationId,
          applicationName: applicationName,
          data: [],
        });
        // Get the binning
        const binning = getStatisticsBinningByDates(props.startDate, props.endDate);

        // Make request to the backend
        const result = await StatisticsRepository.instance.getSystemUsagePerApplicationForUser(
          props.seniorId,
          encodeURIComponent(props.startDate.toISOString()),
          encodeURIComponent(props.endDate.toISOString()),
          applicationId,
          true,
          binning
        );

        // Process
        const processedResult: AreaChartData = result.data.map((obj) => {
          return {
            y: obj.usageTimeInHours,
            x: getXLabelStatisticsDashboardChart(obj.month - 1, obj.year, result.binning, obj.date),
          };
        });

        // Set value
        setApplicationUsage({
          applicationId: applicationId,
          applicationName: applicationName,
          data: processedResult,
        });
      } catch (error: any) {
        // Log
        console.debug(
          `🔴 Could not get the application usage for senior ${props.seniorId} in the last.`,
          error
        );

        // Set error
        setErrorApplicationUsage(error.message);
      } finally {
        // Stop loading
        setLoadingApplicationUsage(false);
      }
    },
    [props.endDate, props.seniorId, props.startDate]
  );

  // Get the application evolution: last X days
  const getApplicationEvolution = useCallback(
    async (applicationId: string, applicationName: string) => {
      try {
        // Start loading
        setLoadingApplicationEvolution(true);

        // Clear error
        setErrorApplicationEvolution(undefined);

        // Update the application and time lapse
        setApplicationEvolution({
          applicationId: applicationId,
          applicationName: applicationName,
          data: [],
        });

        // Make request to the backend
        const result = await StatisticsRepository.instance.getApplicationLevelEvolutionForUser(
          props.seniorId,
          encodeURIComponent(props.startDate.toISOString()),
          encodeURIComponent(props.endDate.toISOString()),
          applicationId
        );

        // Get the binning
        const binning = getStatisticsBinningByDates(props.startDate, props.endDate);

        // Process
        const processedResult: LineChartData = result.data.map((obj) => {
          return {
            y: obj.level,
            x: getXLabelStatisticsDashboardChart(
              moment(obj.date).month(),
              moment(obj.date).year(),
              binning,
              moment(obj.date).date()
            ),
          };
        });

        // Set value
        setApplicationEvolution({
          applicationId: applicationId,
          applicationName: applicationName,
          data: processedResult,
        });
      } catch (error: any) {
        // Log
        console.debug(
          `🔴 Could not get the application evolution for senior ${props.seniorId}.`,
          error
        );

        // Set error
        setErrorApplicationEvolution(error.message);
      } finally {
        // Stop loading
        setLoadingApplicationEvolution(false);
      }
    },
    [props.endDate, props.seniorId, props.startDate]
  );

  // Handle change selected affiliation
  const handleChangeSelectedUserAffiliation = useCallback(
    (value?: SearchSelectOption<AffiliationInfo>) => {
      const newSelectedAffiliation = value?.item;

      if (newSelectedAffiliation) {
        if (newSelectedAffiliation.id !== selectedUserAffiliation?.id) {
          // Set the selected user affiliation
          setSelectedUserAffiliation(newSelectedAffiliation);

          // Set entity id
          entityIdRef.current = newSelectedAffiliation.entity.id;

          // Clear selected application
          setSelectedUserApplication(undefined);

          // Clear application charts
          setApplicationEvolution(undefined);
          setApplicationUsage(undefined);
        }
      } else {
        // Clear selected affiliation
        setSelectedUserAffiliation(undefined);

        // Clear entity id
        entityIdRef.current = undefined;

        // Clear selected application
        setSelectedUserApplication(undefined);

        // Clear application charts
        setApplicationEvolution(undefined);
        setApplicationUsage(undefined);
      }
    },
    [selectedUserAffiliation]
  );

  // Handle change selected application
  const handleChangeSelectedUserApplication = useCallback(
    (value?: SearchSelectOption<CategorizedApplication>) => {
      if (value) {
        if (value.item.application.id !== selectedUserApplication?.application.id) {
          // Set the selected user application
          setSelectedUserApplication(value.item);

          // Clear application evolution if the application is not a game
          if (value.item.category.id !== appIdentifications.categories.games) {
            setApplicationEvolution(undefined);
          }
        }
      } else {
        // Clear selected application
        setSelectedUserApplication(undefined);

        // Clear application charts
        setApplicationEvolution(undefined);
        setApplicationUsage(undefined);
      }
    },
    [selectedUserApplication]
  );

  // Fetch user applications
  const handleFetchUserApplications = useCallback(
    async (
      limit: number,
      skip: number,
      search?: string
    ): Promise<{data: Array<SearchSelectOption<CategorizedApplication>>; total: number}> => {
      // Check entity id
      if (!entityIdRef.current) {
        // Error
        throw new Error('Empty entity id.');
      }

      // Get user applications
      const response = await ApplicationsRepository.instance.getForUserInEntity(
        props.seniorId,
        entityIdRef.current,
        skip,
        limit,
        search
      );

      // Return
      return {
        data: response.data.map((obj) => {
          return {
            id: obj.application.id || '',
            name: obj.application.name || '',
            avatar: obj.application.picture,
            item: obj,
          };
        }),
        total: response.totalCount,
      };
    },
    [props.seniorId]
  );

  // Fetch user affiliations
  const handleFetchUserAffiliations = useCallback(
    async (
      limit: number,
      skip: number
    ): Promise<{data: Array<SearchSelectOption<AffiliationInfo>>; total: number}> => {
      // Get affiliations
      const result = await UsersRepository.instance.getUserAffiliations(
        props.seniorId,
        skip,
        limit
      );

      return {
        data: result.data.map((obj) => {
          return {
            id: obj.id,
            name: obj.entity.name,
            avatar: obj.entity.picture,
            item: obj,
          };
        }),
        total: result.totalCount,
      };
    },
    [props.seniorId]
  );

  // Get all non-date range data
  const getStaticData = () => {
    getNumberInformalCaregivers();
    getNumberPendingInvitations();
    getLastAccess();
    getNumberVideoMessagesSent();
    getNumberVideoMessagesReceived();
    getNumberPhotosSent();
    getNumberDrawingsSent();
    getNumberPaintingsSent();
    getNumberMediaReceived();

    // Reset entity id
    if (props.caregiverMode === CaregiverMode.Professional) {
      // Set entity
      entityIdRef.current = props.entityId;
    } else if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
      // Set entity
      entityIdRef.current = undefined;
    }

    // Clear the selected application
    if (selectedUserApplication) {
      setSelectedUserApplication(undefined);
    }

    // Clear the selected affiliation
    if (selectedUserAffiliation) {
      setSelectedUserAffiliation(undefined);
    }

    // Clear the application usage
    if (applicationUsage) {
      setApplicationUsage(undefined);
    }

    // Clear the application evolution
    if (applicationEvolution) {
      setApplicationEvolution(undefined);
    }
  };

  // Get all date-range data
  const getDynamicData = () => {
    getGeneralSystemUsage();
    getApplicationsUsage();
    getAverageDailyUsage();
    getNumberCalls();
    getNumberIncomingCalls();
    getNumberOutgoingCalls();
    getTopPreferences();
    getMostUsedApplications();
  };

  // Get the application usage and evolution
  const getSelectedApplicationData = () => {
    if (selectedUserApplication?.application.id) {
      getApplicationUsage(
        selectedUserApplication.application.id,
        selectedUserApplication.application.name
      );
      if (selectedUserApplication.category.id === appIdentifications.categories.games) {
        getApplicationEvolution(
          selectedUserApplication.application.id,
          selectedUserApplication.application.name
        );
      }
    }
  };

  // Get all non-date-range related data for the senior
  useEffect(() => {
    // Get data
    getStaticData();

    // Set user affiliations
    if (props.caregiverMode === CaregiverMode.Professional) {
      // Set entity
      entityIdRef.current = props.entityId;
    } else if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
      // Set entity
      entityIdRef.current = undefined;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.seniorId, props.entityId, props.caregiverMode]);

  // Get all date-range related data for the senior
  useEffect(() => {
    getDynamicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.startDate, props.endDate]);

  // Get the application usage and evolution
  useEffect(() => {
    getSelectedApplicationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.startDate, props.endDate, selectedUserApplication]);

  return (
    <>
      {props.showTitle && <PageHeader title={t(translations.senior_statistics_dashboard.title)} />}
      <Box
        className={`senior-statistics-dashboard-container ${
          props.showTitle ? '' : 'senior-statistics-dashboard-container-without-title'
        }`}
      >
        {!props.notShowUpdateButton && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => {
              getStaticData();
              getDynamicData();
              getSelectedApplicationData();
            }}
            disabled={
              loadingNumberInformalCaregivers ||
              loadingNumberPendingInvitations ||
              loadingAverageDailyUsage ||
              loadingLastAccess ||
              loadingNumberOutgoingCallsLast ||
              loadingNumberIncomingCallsLast ||
              loadingNumberCalls ||
              loadingNumberVideoMessagesReceived ||
              loadingNumberVideoMessagesSent ||
              loadingNumberPhotosSent ||
              loadingNumberDrawingsSent ||
              loadingNumberMediaReceived ||
              loadingTopPreferences ||
              loadingGeneralSystemUsage ||
              loadingMostUsedApplications ||
              loadingApplicationsUsage ||
              loadingApplicationEvolution ||
              loadingApplicationUsage
            }
          >
            {t(translations.senior_statistics_dashboard.refresh_button)}
          </Button>
        )}
        <Box className="senior-statistics-dashboard-text-charts-container">
          <TextChart
            value={numberInformalCaregivers}
            title={t(
              translations.senior_statistics_dashboard.text_charts
                .number_of_informal_caregivers_title
            )}
            subtitle={t(
              translations.senior_statistics_dashboard.text_charts
                .number_of_informal_caregivers_subtitle
            )}
            loading={loadingNumberInformalCaregivers}
            handleRefresh={getNumberInformalCaregivers}
            error={errorNumberInformalCaregivers}
          />
          <TextChart
            value={numberPendingInvitations}
            title={t(
              translations.senior_statistics_dashboard.text_charts.number_of_pending_invites_title
            )}
            subtitle={t(
              translations.senior_statistics_dashboard.text_charts
                .number_of_pending_invites_subtitle
            )}
            loading={loadingNumberPendingInvitations}
            handleRefresh={getNumberPendingInvitations}
            error={errorNumberPendingInvitations}
          />
          <TextChart
            value={averageDailyUsage}
            title={t(
              translations.senior_statistics_dashboard.text_charts.average_daily_usage_last_title
            )}
            unitLabel={t(
              translations.senior_statistics_dashboard.text_charts.average_daily_usage_unit_label
            )}
            loading={loadingAverageDailyUsage}
            handleRefresh={() => getAverageDailyUsage()}
            error={errorAverageDailyUsage}
          />
          <TextChart
            value={lastAccess}
            title={t(
              translations.senior_statistics_dashboard.text_charts.last_access_date_time_title
            )}
            loading={loadingLastAccess}
            handleRefresh={getLastAccess}
            error={errorLastAccess}
          />
          <TextChart
            value={numberOutgoingCallsLast}
            title={t(
              translations.senior_statistics_dashboard.text_charts.number_of_outgoing_calls_title
            )}
            subtitle={t(
              translations.senior_statistics_dashboard.text_charts.number_of_outgoing_calls_subtitle
            )}
            loading={loadingNumberOutgoingCallsLast}
            handleRefresh={() => getNumberOutgoingCalls()}
            error={errorNumberOutgoingCallsLast}
          />
          <TextChart
            value={numberIncomingCallsLast}
            title={t(
              translations.senior_statistics_dashboard.text_charts.number_of_incoming_calls_title
            )}
            subtitle={t(
              translations.senior_statistics_dashboard.text_charts.number_of_incoming_calls_subtitle
            )}
            loading={loadingNumberIncomingCallsLast}
            handleRefresh={() => getNumberIncomingCalls()}
            error={errorNumberIncomingCallsLast}
          />
          <TextChart
            value={numberCalls}
            title={t(translations.senior_statistics_dashboard.text_charts.number_of_calls_title)}
            subtitle={t(
              translations.senior_statistics_dashboard.text_charts.number_of_calls_subtitle
            )}
            loading={loadingNumberCalls}
            handleRefresh={() => getNumberCalls()}
            error={errorNumberCallsLast}
          />
          <TextChart
            value={numberVideoMessagesSent}
            title={t(
              translations.senior_statistics_dashboard.text_charts
                .number_of_video_messages_sent_title
            )}
            loading={loadingNumberVideoMessagesSent}
            handleRefresh={getNumberVideoMessagesSent}
            error={errorNumberVideoMessagesSent}
          />
          <TextChart
            value={numberVideoMessagesReceived}
            title={t(
              translations.senior_statistics_dashboard.text_charts
                .number_of_video_messages_received_title
            )}
            loading={loadingNumberVideoMessagesReceived}
            handleRefresh={getNumberVideoMessagesReceived}
            error={errorNumberVideoMessagesReceived}
          />
          <TextChart
            value={numberPhotosSent}
            title={t(
              translations.senior_statistics_dashboard.text_charts.number_of_photos_sent_title
            )}
            loading={loadingNumberPhotosSent}
            handleRefresh={getNumberPhotosSent}
            error={errorNumberPhotosSent}
          />
          <TextChart
            value={numberDrawingsSent}
            title={t(
              translations.senior_statistics_dashboard.text_charts.number_of_drawings_sent_title
            )}
            loading={loadingNumberDrawingsSent}
            handleRefresh={getNumberDrawingsSent}
            error={errorNumberDrawingsSent}
          />
          <TextChart
            value={numberPaintingsSent}
            title={t(
              translations.senior_statistics_dashboard.text_charts.number_of_paintings_sent_title
            )}
            loading={loadingNumberPaintingsSent}
            handleRefresh={getNumberPaintingsSent}
            error={errorNumberPaintingsSent}
          />
          <TextChart
            value={numberMediaReceived}
            title={t(
              translations.senior_statistics_dashboard.text_charts.number_of_media_received_title
            )}
            subtitle={t(
              translations.senior_statistics_dashboard.text_charts.number_of_media_received_subtitle
            )}
            loading={loadingNumberMediaReceived}
            handleRefresh={getNumberMediaReceived}
            error={errorNumberMediaReceived}
          />
        </Box>
        <Box className="senior-statistics-dashboard-table-charts-container">
          <TableChart
            rows={topPreferences}
            columns={[
              {
                columnId: TopPreferencesColumnIdentifier.Activity,
                title: t(
                  translations.senior_statistics_dashboard.table_charts
                    .top_preferences_application_column
                ),
                width: '60%',
                enableSort: true,
              },
              {
                columnId: TopPreferencesColumnIdentifier.Total,
                title: t(
                  translations.senior_statistics_dashboard.table_charts
                    .top_preferences_total_usage_time_column
                ),
                width: '40%',
                enableSort: true,
                unitLabel: t(
                  translations.senior_statistics_dashboard.table_charts
                    .top_preferences_total_usage_time_column_unit_label
                ),
              },
            ]}
            title={t(translations.senior_statistics_dashboard.table_charts.top_preferences_title)}
            subtitle={t(
              translations.senior_statistics_dashboard.table_charts.top_preferences_subtitle
            )}
            loading={loadingTopPreferences}
            handleRefresh={() => getTopPreferences()}
            error={errorTopPreferences}
          />
        </Box>
        <Box className="senior-statistics-dashboard-area-charts-container-1">
          <AreaChart
            data={generalSystemUsage}
            title={t(
              translations.senior_statistics_dashboard.area_charts.general_system_usage_title
            )}
            subtitle={t(
              translations.senior_statistics_dashboard.area_charts.general_system_usage_subtitle
            )}
            xLabel={t(
              translations.senior_statistics_dashboard.area_charts.general_system_usage_x_label
            )}
            yLabel={t(
              translations.senior_statistics_dashboard.area_charts.general_system_usage_y_label
            )}
            yUnitLabel={t(
              translations.senior_statistics_dashboard.area_charts.general_system_usage_y_unit_label
            )}
            loading={loadingGeneralSystemUsage}
            handleRefresh={() => getGeneralSystemUsage()}
            yDomain={[0, Math.max(...generalSystemUsage.map((obj) => obj.y)) + 5]}
            error={errorGeneralSystemUsage}
          />
        </Box>
        <Box className="senior-statistics-dashboard-pie-charts-container">
          <PieChart
            data={mostUsedApplications}
            title={t(
              translations.senior_statistics_dashboard.pie_charts.most_used_applications_title
            )}
            subtitle={t(
              translations.senior_statistics_dashboard.pie_charts.most_used_applications_subtitle
            )}
            xLabel={t(
              translations.senior_statistics_dashboard.pie_charts.most_used_applications_x_label
            )}
            yLabel={t(
              translations.senior_statistics_dashboard.pie_charts.most_used_applications_y_label
            )}
            yUnitLabel={t(
              translations.senior_statistics_dashboard.pie_charts
                .most_used_applications_y_unit_label
            )}
            loading={loadingMostUsedApplications}
            handleRefresh={() => getMostUsedApplications()}
            error={errorMostUsedApplications}
          />
        </Box>
        <Box className="senior-statistics-dashboard-stack-charts-container">
          <StackChart
            data={applicationsUsage}
            title={t(
              translations.senior_statistics_dashboard.stack_charts.applications_usage_title
            )}
            subtitle={t(
              translations.senior_statistics_dashboard.stack_charts.applications_usage_subtitle
            )}
            xLabel={t(
              translations.senior_statistics_dashboard.stack_charts.applications_usage_x_label
            )}
            yLabel={t(
              translations.senior_statistics_dashboard.stack_charts.applications_usage_y_label
            )}
            yUnitLabel={t(
              translations.senior_statistics_dashboard.stack_charts.applications_usage_y_unit_label
            )}
            stackLabel={{
              plural: t(
                translations.senior_statistics_dashboard.stack_charts
                  .applications_usage_plural_stack_label
              ),
              singular: t(
                translations.senior_statistics_dashboard.stack_charts
                  .applications_usage_singular_stack_label
              ),
            }}
            loading={loadingApplicationsUsage}
            handleRefresh={() => getApplicationsUsage()}
            error={errorApplicationsUsage}
          />
        </Box>
        <Divider sx={{width: '100%', mt: '16px'}} />
        <Card
          variant="outlined"
          className="senior-statistics-dashboard-evolution-per-application-container"
        >
          <Typography
            variant="h6"
            component="p"
            className="senior-statistics-dashboard-evolution-per-application-title"
          >
            {t(translations.senior_statistics_dashboard.statistics_per_application_title)}
          </Typography>
          <Box className="senior-statistics-dashboard-evolution-per-application-selects-container">
            {props.caregiverMode === CaregiverMode.SystemAdministrator && (
              <FlatSearchSelectInput<AffiliationInfo>
                className="senior-statistics-dashboard-evolution-per-application-select-container"
                label={t(
                  translations.senior_statistics_dashboard.statistics_per_application_entity_label
                )}
                value={
                  selectedUserAffiliation
                    ? {
                        id: selectedUserAffiliation.id || '',
                        name: selectedUserAffiliation.entity.name || '',
                        item: selectedUserAffiliation,
                        avatar: selectedUserAffiliation.entity.picture,
                      }
                    : undefined
                }
                avatar={{
                  type: 'entity',
                  isIcon: false,
                }}
                fetchWhenEmpty
                handleChange={handleChangeSelectedUserAffiliation}
                handleFetchData={handleFetchUserAffiliations}
              />
            )}
            <FlatSearchSelectInput<CategorizedApplication>
              className="senior-statistics-dashboard-evolution-per-application-select-container"
              label={t(
                translations.senior_statistics_dashboard
                  .statistics_per_application_application_label
              )}
              value={
                selectedUserApplication
                  ? {
                      id: selectedUserApplication.application.id || '',
                      name: selectedUserApplication.application.name || '',
                      item: selectedUserApplication,
                      avatar: selectedUserApplication.application.picture,
                    }
                  : undefined
              }
              handleChange={handleChangeSelectedUserApplication}
              handleFetchData={handleFetchUserApplications}
              fetchWhenEmpty={true}
              avatar={{
                type: 'other',
                isIcon: false,
              }}
            />
          </Box>
          {props.caregiverMode === CaregiverMode.SystemAdministrator && (
            <Alert
              severity="info"
              className="senior-statistics-dashboard-evolution-per-application-info-message"
            >
              {t(translations.senior_statistics_dashboard.statistics_per_application_info_message)}
            </Alert>
          )}
        </Card>
        {applicationUsage && (
          <Box className="senior-statistics-dashboard-area-charts-container-2">
            <AreaChart
              data={applicationUsage.data}
              title={t(
                translations.senior_statistics_dashboard.area_charts.application_usage_title,
                {
                  name: applicationUsage.applicationName,
                }
              )}
              subtitle={t(
                translations.senior_statistics_dashboard.area_charts.application_usage_subtitle
              )}
              xLabel={t(
                translations.senior_statistics_dashboard.area_charts.application_usage_x_label
              )}
              yLabel={t(
                translations.senior_statistics_dashboard.area_charts.application_usage_y_label
              )}
              yUnitLabel={t(
                translations.senior_statistics_dashboard.area_charts.application_usage_y_unit_label
              )}
              loading={loadingApplicationUsage}
              handleRefresh={() =>
                getApplicationUsage(
                  applicationUsage.applicationId,
                  applicationUsage.applicationName
                )
              }
              yDomain={[0, Math.max(...applicationUsage.data.map((obj) => obj.y)) + 5]}
              error={errorApplicationUsage}
            />
          </Box>
        )}
        {applicationEvolution && (
          <Box className="senior-statistics-dashboard-line-charts-container">
            <LineChart
              data={applicationEvolution.data}
              title={t(
                translations.senior_statistics_dashboard.line_charts.application_evolution_title,
                {
                  name: applicationEvolution.applicationName,
                }
              )}
              subtitle={t(
                translations.senior_statistics_dashboard.line_charts.application_evolution_subtitle
              )}
              xLabel={t(
                translations.senior_statistics_dashboard.line_charts.application_evolution_x_label
              )}
              yLabel={t(
                translations.senior_statistics_dashboard.line_charts.application_evolution_y_label
              )}
              loading={loadingApplicationEvolution}
              handleRefresh={() =>
                getApplicationEvolution(
                  applicationEvolution.applicationId,
                  applicationEvolution.applicationName
                )
              }
              yDomain={[0, Math.max(...applicationEvolution.data.map((obj) => obj.y)) + 1]}
              error={errorApplicationEvolution}
            />
          </Box>
        )}
      </Box>
    </>
  );
};
